import {AccentColorOptions} from 'epic-ue-theme';
import {SvgIconColor} from '../../';
import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {FacebookIcon} from './FacebookIcon';
import {LinkedInIcon} from './LinkedInIcon';
import {TwitterIcon} from './TwitterIcon';
import {RedditIcon} from './RedditIcon';
import {TumblrIcon} from './TumblrIcon';
import {VkIcon} from './VkIcon';
import {WeiboIcon} from './WeiboIcon';
import {WeChatIcon} from './WeChatIcon';
import {UrlIcon} from './UrlIcon';
import root from 'window-or-global';
import {useClipboard} from 'use-clipboard-copy';
import {getDataAttribute, getBowserState, getLocale} from '@epic-core/common';
import QRCode from 'qrcode.react';
import {emitEvent} from 'epic-ue-shared';

const SocialIcon = styled.div`
    display: inline-block;
    font-weight: 400;
    font-size: 20px;
    line-height: 28px;
    margin: 0;
    color: ${(p) => p.theme.palette.text.primary};
    transition: color 0.2s ${(p) => p.theme.easing};
`;

const Styled = styled.div<{accentColor: AccentColorOptions}>`
    .icon-list {
        display: flex;
        flex-direction: column;
    }
    .social-icon-grid {
        padding-bottom: 13px;
        cursor: pointer;
        text-align: right;
        display: inline-block;

        ${SocialIcon}:hover {
            color: ${(p) => p.theme.palette.accent[p.accentColor]};
            svg {
                transition: color 0.2s ${(p) => p.theme.easing};
                color: ${(p) => p.theme.palette.accent[p.accentColor]} !important;
            }
        }
    }
`;
interface SocialIconsStackedProps {
    accentColor?: AccentColorOptions;
    iconColor?: SvgIconColor;
    title?: string;
    tweet?: string;
    copyMessageStr?: string;
    fontSize?: 'small' | 'inherit' | 'medium' | 'large';
}

export const SocialIconsStacked = (props: SocialIconsStackedProps): JSX.Element | null => {
    const {accentColor = 'sky', fontSize = 'medium', tweet = '', title, iconColor} = props;
    const color = iconColor || 'primary';
    const clipboard = useClipboard();
    const [showWeChatQr, setShowWeChatQr] = useState(false);

    const hideQr = useCallback(
        (e) => {
            setShowWeChatQr(false);
        },
        [setShowWeChatQr]
    );

    const clipboardHandle = useCallback(
        (e) => {
            const shareUrl = root.location.href;
            clipboard.copy(shareUrl);
        },
        [clipboard.copy]
    );

    const socialClicked = useCallback(
        (e) => {
            e.preventDefault();
            const socialPlatform = getDataAttribute(e.currentTarget, 'platform');
            emitEvent({
                eventAction: 'social.icon.click',
                eventLabel: 'SocialIconsStacked',
                eventValue: socialPlatform
            });
            let shareUrl = root.location.href;

            switch (socialPlatform) {
                case 'twitter':
                    shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        tweet
                    )}&url=${encodeURIComponent(shareUrl)}`;
                    break;
                case 'facebook':
                    shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        shareUrl
                    )}`;
                    break;
                case 'vk':
                    shareUrl = `http://vk.com/share.php?url=${encodeURIComponent(shareUrl)}`;
                    break;
                case 'linkedin':
                    shareUrl = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(
                        shareUrl
                    )}`;
                    break;
                case 'tumblr':
                    shareUrl = `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${encodeURIComponent(
                        shareUrl
                    )}`;
                    break;
                case 'reddit':
                    shareUrl = `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}`;
                    break;
                case 'weibo':
                    shareUrl = `http://service.weibo.com/share/share.php?url=${encodeURIComponent(
                        shareUrl
                    )}&title=${title}`;
                    break;
                case 'wechat':
                    setShowWeChatQr(true);
                    break;
                default:
                    break;
            }
            if (socialPlatform !== 'wechat' && socialPlatform !== 'link') {
                root.open(shareUrl, '_blank', 'toolbar=no, resizeable=yes, width=600, height=450');
            }
        },
        [title, tweet, setShowWeChatQr]
    );

    const socialIconList: (JSX.Element | undefined)[] = [<div key="socialList" />];
    const language = root.navigator.language;
    const locale = getLocale();
    const {mobile} = getBowserState();

    let platformList = ['facebook', 'twitter', 'linkedin', 'reddit', 'tumblr', 'link'];
    if (locale === 'zh-cn' || language === 'zh-cn' || language === 'zh') {
        platformList = ['weibo', 'wechat'];
    } else if (language === 'ru') {
        platformList = ['vk', 'facebook', 'twitter', 'linkedin', 'reddit'];
    }

    platformList.map((platform) => {
        if (platform && platform !== 'link') {
            let socialIcon: JSX.Element = <div key={'no-social'} />;

            switch (platform) {
                case 'twitter':
                    socialIcon = <TwitterIcon fontSize={fontSize} color={color} />;
                    break;
                case 'facebook':
                    socialIcon = <FacebookIcon fontSize={fontSize} color={color} />;
                    break;
                case 'vk':
                    socialIcon = <VkIcon fontSize={fontSize} color={color} />;
                case 'linkedin':
                    socialIcon = <LinkedInIcon fontSize={fontSize} color={color} />;
                    break;
                case 'tumblr':
                    socialIcon = <TumblrIcon fontSize={fontSize} color={color} />;
                    break;
                case 'reddit':
                    socialIcon = <RedditIcon fontSize={fontSize} color={color} />;
                    break;
                case 'weibo':
                    socialIcon = <WeiboIcon fontSize={fontSize} color={color} />;
                    break;
                case 'wechat':
                    socialIcon = <WeChatIcon fontSize={fontSize} color={color} />;
                    setShowWeChatQr(true);
                    break;
                default:
                    break;
            }

            socialIconList.push(
                <div
                    role="button"
                    tabIndex={-1}
                    className="social-icon-grid"
                    data-platform={platform}
                    key={`social-${platform}`}
                    onClick={socialClicked}>
                    <SocialIcon>{socialIcon}</SocialIcon>
                </div>
            );
        } else if (platform && platform === 'link') {
            socialIconList.push(
                <div
                    role="button"
                    tabIndex={-1}
                    className="social-icon-grid"
                    data-platform={platform}
                    key={`social-${platform}`}
                    onClick={clipboardHandle}>
                    <SocialIcon>
                        <UrlIcon fontSize={fontSize} color={color} />
                    </SocialIcon>
                </div>
            );
        }
    });

    return (
        <Styled className={`${mobile ? ' isMobile' : ''}`} accentColor={accentColor}>

            <div className="icon-list">
                {socialIconList}
            </div>

            {showWeChatQr ? (
                <div role="presentation" className="qrcode-block" onClick={hideQr}>
                    <QRCode value={root.location.href} />
                </div>
            ) : (
                ''
            )}
        </Styled>
    );
};
